/**
 * Derives the gateway API host from the current page's location.
 *
 * Canary URLs (`tav-XXXX.canary.turnpoint.co`) map to the matching
 * `tav-XXXX.canary-api.turnpoint.co`, overriding any baked
 * `REACT_APP_API_HOST`. A per-canary web bundle must always pair with its
 * own per-canary API — pairing with the staging/uat/prod API would defeat
 * the canary's purpose. See TAV-2481.
 *
 * Otherwise `REACT_APP_API_HOST` (set at build time per environment) wins,
 * falling back to the current origin for local dev.
 *
 * Exported as a pure function for unit testing; production use evaluates
 * once at module load below.
 */
export const deriveApiHost = (hostname: string, protocol: string): string => {
  if (hostname.includes('.canary.')) {
    return `${protocol}//${hostname.replace('.canary.', '.canary-api.')}`;
  }
  return process.env.REACT_APP_API_HOST || `${protocol}//${hostname}`;
};

// Set Gateway API parameters from Environment
const API_HOST = deriveApiHost(window.location.hostname, window.location.protocol);
const API_PORT = process.env.REACT_APP_API_PORT;

// Set Gateway API URL from Environment
const url = `${API_HOST}${API_PORT ? `:${API_PORT}` : ''}`;

// SSE host for real-time event notifications
export const sseHost = process.env.REACT_APP_SSE_HOST ?? '';

// API constants
const apiPrefix = `${url}/v2`;

/**
 * Appointments
 */
export const apiAppointments = `${apiPrefix}/appointments`;
export const apiAppointmentActivities = `${apiAppointments}/activities`;
export const apiAppointmentCancellationReasons = `${apiAppointments}/cancellation-reasons`;
export const apiAppointmentRepeatFrequencies = `${apiAppointments}/repeat-frequencies`;
export const apiAppointmentServiceTypes = `${apiAppointments}/service-types`;
export const apiAppointmentStatuses = `${apiAppointments}/statuses`;
export const apiGroupAppointments = `${apiAppointments}/groups`;
export const apiAppointmentNoteTypes = `${apiPrefix}/clients/note-types`;

/**
 * Clients
 */
export const apiClients = `${apiPrefix}/clients`;
export const apiClientPackages = `${apiClients}/packages`;
export const apiClientReferrers = `${apiClients}/referrers`;
export const apiClientPackageSchedules = `${apiClients}/packages/schedules`;
export const apiClientProspects = `${apiClients}/prospects`;

/**
 * Support at Home
 */

// root
export const apiSah = `${apiPrefix}/sah`;

// sah/clients
export const apiSahClients = `${apiSah}/clients`;
export const apiSahExternalBudgets = `${apiSahClients}/external/budgets`;
export const apiSahExternalContributions = `${apiSahClients}/external/contributions`;
export const apiSahExternalServices = `${apiSahClients}/external/services`;
export const apiSahClientHcpUnspentFunds = `${apiSahClients}/hcp-unspent-funds`;
export const apiSahBudgets = `${apiSahClients}/budgets`;
export const apiSahStatements = `${apiSahClients}/statements`;
export const apiSahClientInvoices = `${apiSahClients}/invoices`;

// sah/reports
export const apiSahReports = `${apiSah}/reports`;

// sah/companies
export const apiSahCompanies = `${apiSah}/companies`;
export const apiSahClaims = `${apiSahCompanies}/claims`;
export const apiSahClaimItems = `${apiSahClaims}/items`;
export const apiSahReconciliations = `${apiSahCompanies}/reconciliations`;
export const apiSahInvoiceAccountCodes = `${apiSahCompanies}/invoices/account-codes`;
export const apiSahSuppliers = `${apiSahCompanies}/suppliers`;
export const apiSahSupplierInvoices = `${apiSahCompanies}/suppliers/invoices`;
export const apiSahPricePlans = `${apiSahCompanies}/price-plans`;
export const apiSahServiceTypeAssociations = `${apiSahCompanies}/service-type-associations`;
export const apiSahShifts = `${apiSahCompanies}/shifts`;

/**
 * Users
 */
export const apiUsers = `${apiPrefix}/users`;
export const apiAllowances = `${apiUsers}/allowances`;
export const apiUserDepartments = `${apiUsers}/departments`;
export const apiUserLevels = `${apiUsers}/levels`;
export const apiUserSendDetails = `${apiUsers}/send-details`;
export const apiUserPayGroups = `${apiUsers}/pay-groups`;
export const apiUserPayLevels = `${apiUsers}/pay-levels`;

/**
 * Weekly Planner
 */
export const apiWeeklyPlanner = `${apiPrefix}/weekly-planner`;
export const apiWeeklyPlannerAppointments = `${apiWeeklyPlanner}/appointments`;
export const apiWeeklyPlannerAppointmentConflicts = `${apiWeeklyPlanner}/appointments/conflicts`;
export const apiWeeklyPlannerAppointmentMissingQualifications = `${apiWeeklyPlanner}/appointments/missing-qualifications`;
export const apiWeeklyPlannerAppointmentsRejected = `${apiWeeklyPlanner}/appointments/rejected`;
export const apiWeeklyPlannerAppointmentsPublish = `${apiWeeklyPlanner}/publish`;
export const apiWeeklyPlannerAppointmentsPublishEstimates = `${apiWeeklyPlanner}/publish/estimates`;
export const apiWeeklyPlannerAppointmentUnallocated = `${apiWeeklyPlanner}/appointments/unallocated`;
export const apiWeeklyPlannerAppointmentBulkAssignees = `${apiWeeklyPlanner}/appointments/bulk-assignees`;

/**
 * Dashboard
 */
export const apiDashboard = `${apiPrefix}/dashboard`;
export const apiSnapshots = `${apiDashboard}/snapshots`;
export const apiCalendar = `${apiDashboard}/calendar`;

/**
 * Lookups
 */
export const apiAddressLookup = `${apiPrefix}/address-lookup`;
export const apiCalendarFrequencies = `${apiPrefix}/calendar-frequencies`;
export const apiCareQualificationLevels = `${apiPrefix}/care-qualification-levels`;
export const apiClientFormOptions = `${apiPrefix}/client-form-options`;
export const apiEquipments = `${apiPrefix}/equipments`;
export const apiEvents = `${apiPrefix}/events`;
export const apiExternalStatuses = `${apiPrefix}/integrations/statuses`;
export const apiInvoiceStatuses = `${apiPrefix}/invoice-statuses`;
export const apiClientInvoiceStatuses = `${apiPrefix}/client-invoice-statuses`;
export const apiStatementStatuses = `${apiPrefix}/statement-statuses`;
export const apiStatementDeliveryPreferences = `${apiPrefix}/statement-delivery-preferences`;
export const apiInvoiceStatusTransitions = `${apiPrefix}/invoice-statuses/transitions`;
export const apiFundingLevelAmounts = `${apiPrefix}/funding-level-amounts`;
export const apiFundingTypes = `${apiPrefix}/funding-types`;
export const apiClientBudgetStatuses = `${apiPrefix}/client-budget-statuses`;
export const apiLeaveCodes = `${apiPrefix}/leave-codes`;
export const apiLeaveRequests = `${apiPrefix}/leave-requests`;
export const apiLocations = `${apiPrefix}/locations`;
export const apiMedications = `${apiPrefix}/medications`;
export const apiNDISSupportItems = `${apiPrefix}/ndis/support-items`;
export const apiPaymentTypes = `${apiPrefix}/payment-types`;
export const apiRegions = `${apiPrefix}/regions`;
export const apiSahCategories = `${apiPrefix}/sah-categories`;
export const apiSahClaimStatuses = `${apiPrefix}/sah-claim-statuses`;
export const apiSahServiceItems = `${apiPrefix}/sah-service-items`;
export const apiSahServiceWraps = `${apiPrefix}/sah-service-wraps`;
export const apiSahServiceHpts = `${apiPrefix}/sah-service-hpts`;
export const apiSahServices = `${apiPrefix}/sah-services`;
export const apiServiceBranches = `${apiPrefix}/service-branches`;
export const apiShifts = `${apiPrefix}/shifts`;
export const apiStates = `${apiPrefix}/states`;
export const apiTaskClassifications = `${apiPrefix}/tasks/classifications`;
export const apiTaskTypes = `${apiPrefix}/tasks/types`;
export const apiUserLanguages = `${apiPrefix}/users/languages`;
export const apiSupplementsLookup = `${apiPrefix}/budget-supplement-rates`;

// Unaliased / root level endpoints
export const apiAccessTags = `${apiPrefix}/settings/access-tags`;
export const apiCompanyAccessTokens = `${apiPrefix}/companies/access-tokens`;
export const apiCompanySettings = `${apiPrefix}/companies/settings`;
export const apiAuthenticateSession = `${apiPrefix}/authenticate-session`;
export const apiDocumentBatchesApi = `${apiPrefix}/documents/batches`;
export const apiFeatureFlags = `${apiPrefix}/feature-flags`;
export const apiGenerateSession = `${apiPrefix}/generate-session`;
export const apiLog = `${apiPrefix}/log`;
export const apiLogin = `${apiPrefix}/login`;
export const apiSettings = `${apiPrefix}/settings`;
export const apiUserFeedback = `${apiPrefix}/user-feedbacks`;

export const apiCompanies = `${apiPrefix}/companies`;
export const apiWorkSites = `${apiPrefix}/companies/worksites`;
export const apiFormDefaults = `${apiPrefix}/companies/forms/defaults`;

/**
 * GoCardless / Direct Debits
 */
export const apiGocardless = `${apiPrefix}/gocardless`;
export const apiGocardlessConnections = `${apiGocardless}/connections`;
export const apiGocardlessOAuth = `${apiGocardless}/oauth`;
export const apiGocardlessClientSignups = `${apiGocardless}/client-signups`;
export const apiGocardlessMandates = `${apiGocardless}/mandates`;
export const apiGocardlessPaymentBatches = `${apiGocardless}/payment-batches`;

/* GoCardless lookups */
export const apiPaymentBatchStatuses = `${apiPrefix}/payment-batch-statuses`;
export const apiPaymentStatuses = `${apiPrefix}/payment-statuses`;

/**
 * RoR.E
 */
export const apiRore = `${apiPrefix}/rore`;
export const apiRoreSessions = `${apiRore}/sessions`;

/**
 * Forms
 */
export const apiForms = `${apiPrefix}/forms`;
export const apiFormsTemplates = `${apiForms}/templates`;
export const apiFormsMergeFields = `${apiForms}/merge-fields`;
